import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import { useIsTextTruncated } from '../../hooks/useIsTextTruncated';
import { SearchableSelectMenu, type SearchableSelectOption } from './SearchableSelectMenu';

export type { SearchableSelectOption };

interface SearchableSelectBaseProps {
    /** Accessible name of the list, and of the trigger when no `id` ties it to a `<label>`. */
    label: string;
    /** Trigger id, so a `<label htmlFor>` names it. */
    id?: string;
    /** Trigger text while nothing is selected. */
    placeholder: string;
    searchPlaceholder: string;
    options: readonly SearchableSelectOption[];
    disabled?: boolean;
    /** Replaces the default trigger look, e.g. to match the surrounding form inputs. */
    triggerClassName?: string;
}

export type SearchableSelectProps = SearchableSelectBaseProps &
    (
        | { mode: 'multiple'; selected: readonly string[]; onChange: (next: string[]) => void }
        | { mode: 'single'; value: string | null; onChange: (value: string) => void }
    );

const TRIGGER_LAYOUT =
    'w-full flex items-center gap-2 text-left disabled:cursor-not-allowed disabled:bg-gray-50';
const DEFAULT_TRIGGER_LOOK =
    'h-9 rounded-lg border border-gray-200 bg-white px-3 text-xs font-medium hover:border-gray-400 aria-expanded:border-gray-800 transition-colors duration-200';

interface TriggerTextProps {
    options: readonly SearchableSelectOption[];
    selectedValues: readonly string[];
    single: boolean;
    placeholder: string;
}

// One line with one ellipsis; the full text shows on hover only when it is cropped.
function TriggerText({ options, selectedValues, single, placeholder }: TriggerTextProps) {
    const [element, setElement] = useState<HTMLSpanElement | null>(null);
    const chosen = options.filter((option) => selectedValues.includes(option.value));
    const [first] = chosen;
    const fullText =
        single && first
            ? [first.label, first.sublabel].filter(Boolean).join(' · ')
            : chosen.map((option) => option.label).join(', ');
    const truncated = useIsTextTruncated(element, fullText);
    return (
        <Tooltip content={fullText} disabled={!truncated}>
            <span
                ref={setElement}
                className={`flex-1 min-w-0 truncate ${chosen.length ? 'text-gray-800' : 'text-gray-400'}`}
            >
                {chosen.length === 0 && placeholder}
                {single && first && (
                    <>
                        {first.label}
                        {first.sublabel && (
                            <span className="text-gray-500"> · {first.sublabel}</span>
                        )}
                    </>
                )}
                {!single && fullText}
            </span>
        </Tooltip>
    );
}

// One searchable dropdown for both single picks (form fields) and multi-select filters.
export function SearchableSelect(props: SearchableSelectProps) {
    const { label, id, placeholder, searchPlaceholder, options, disabled, triggerClassName } =
        props;
    const [open, setOpen] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const listId = useId();
    const single = props.mode === 'single';
    const selectedValues =
        props.mode === 'single' ? (props.value === null ? [] : [props.value]) : props.selected;

    const close = () => setOpen(false);
    const pick = (value: string) => {
        if (props.mode === 'single') {
            props.onChange(value);
            close();
            triggerRef.current?.focus();
            return;
        }
        const { selected, onChange } = props;
        onChange(
            selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]
        );
    };
    const onTriggerKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape' && open) {
            e.stopPropagation();
            close();
        } else if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
        }
    };

    return (
        <>
            <button
                ref={triggerRef}
                id={id}
                type="button"
                disabled={disabled}
                aria-label={id ? undefined : label}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listId : undefined}
                onClick={() => setOpen((prev) => !prev)}
                onKeyDown={onTriggerKeyDown}
                className={`${TRIGGER_LAYOUT} ${triggerClassName ?? DEFAULT_TRIGGER_LOOK}`}
            >
                <TriggerText
                    options={options}
                    selectedValues={selectedValues}
                    single={single}
                    placeholder={placeholder}
                />
                {!single && selectedValues.length > 0 && (
                    <span className="shrink-0 rounded-full bg-gray-900 px-1.5 py-px text-[10px] font-semibold text-white">
                        {selectedValues.length}
                    </span>
                )}
                <ChevronDownIcon
                    className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
                />
            </button>
            {open && (
                <SearchableSelectMenu
                    triggerRef={triggerRef}
                    listId={listId}
                    label={label}
                    searchPlaceholder={searchPlaceholder}
                    options={options}
                    multiple={!single}
                    isSelected={(value) => selectedValues.includes(value)}
                    onPick={pick}
                    onClear={
                        props.mode === 'multiple' && props.selected.length > 0
                            ? () => props.onChange([])
                            : undefined
                    }
                    onClose={close}
                />
            )}
        </>
    );
}
